import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import HeadlessInput from '../../../shared/component/headless/input/HeadlessInput';
import { MemberInfoValueMobile } from '../../../shared/component/headless/input/InputBody';
import HeadlessBtn from '../../../shared/component/headless/button/HeadlessBtn';
import { BlackFullWidthBtn } from '../../../shared/component/headless/button/BtnBody';
import { useModalStore } from '../../../shared/store/portal/modalStore';
import { useValidation } from '../../../shared/hooks/useValidation';
import Space from '../../../shared/Space';
import { useMember } from '../../../shared/hooks/useApi/useMember';
import { useAuth } from '../../../shared/hooks/useApi/useAuth';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import searchIcon from '../../../asset/icon/see.png';
import searchNotIcon from '../../../asset/icon/see_not.png';
import belowIcon from '../../../asset/icon/below.png';
import { getModalMessage } from '../../../shared/constants/modalMessages';
import { getBtnName } from '../../../shared/constants/btnNames';

interface ModalProps {
  setIsUserInput: (isUserInput: boolean) => void;
}

const ChangePWModalMobile: React.FC<ModalProps> = ({ setIsUserInput }) => {
  const { user } = useAuth();
  const { standardModal, clearModal } = useModalStore();
  const { updatePassword } = useMember();
  const { 
    PASSWORD_REGEX,
    explainPasswordIssues
  } = useValidation();
  const [showPassword, setShowPassword] = useState(false);
  const [pw, setPw] = useState(standardModal?.value || "");
  const [newPw, setNewPw] = useState("");
  const [error, setError] = useState('');

  useEffect(() => {
    if (pw.length > 0 || newPw.length > 0) {
      setIsUserInput(true);
    } else {
      setIsUserInput(false);
    }
  }, [pw, newPw]);

  if (user == null || standardModal == null) return null;

  const handleSubmit = async () => {
    if (!validateNewPassword(newPw)) {
      return;
    }
    setError('');

    await updatePassword({
      id: user.id,
      password: pw,
      newPassword: newPw,
    });
  };

  const validateNewPassword = (value: string) => {
    if (value.length === 0) {
      setError('');
      return true;
    }
    if (!PASSWORD_REGEX.test(value)) {
      const issues = explainPasswordIssues(value);
      setError(issues[0] ?? 'Password needs a little adjustment.');
      return false;
    }
    setError('');
    return true;
  };

  const changeNewPasswordInput = (value: string) => {
    validateNewPassword(value);
    setNewPw(value);
  }


  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
      <ChangeModalComp>
        <TitleWrapper>
          <TitleGroup>
            <Title>{getModalMessage('CHANGE_PASSWORD_TITLE')}</Title>
            <UniversalIconBtn
              size="standard"
              border={false}
              isTransparent
              backgroundImg={showPassword ? searchIcon : searchNotIcon}
              onClick={togglePasswordVisibility}
            />
          </TitleGroup>
          <UniversalIconBtn
            size="fit"
            border={false}
            isTransparent
            backgroundImg={belowIcon}
            onClick={() => clearModal(400)}
          />
        </TitleWrapper>
        <HeadlessInput
          type={showPassword ? 'text' : 'password'}
          value={pw}
          handleChange={(e) => setPw(e.target.value)}
          placeholder={getModalMessage('ENTER_CURRENT_PASSWORD')}
          StyledInput={MemberInfoValueMobile}
        />
        <HeadlessInput
          type={showPassword ? 'text' : 'password'}
          value={newPw}
          handleChange={(e) => changeNewPasswordInput(e.target.value)}
          placeholder={getModalMessage('CREATE_NEW_PASSWORD')}
          StyledInput={MemberInfoValueMobile}
        />
        <Space $height="20px">
          {error && <ErrorText>{error}</ErrorText>}
        </Space>
        <HeadlessBtn
          value={getBtnName('CHANGE')}
          handleClick={handleSubmit}
          StyledBtn={SubmitButton}
        />
      </ChangeModalComp>
  );
};


const ChangeModalComp = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  gap: 14px;
`

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  margin-top: clamp(12px, 2vh, 30px);
  margin-bottom: clamp(20px, 8vh, 30px);
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Title = styled.h2`
  ${({ theme }) => theme.typography.Body_01_1};
  color: ${({ theme }) => theme.colors.color_Gray_02};
`;


const SubmitButton = styled(BlackFullWidthBtn)`
  margin: 20px 0px 15px 0px;
`;

const ErrorText = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_alert_red};
  ${({ theme }) => theme.typography.Body_04};
`;

export default ChangePWModalMobile;
